import { useId, useState } from 'react';
import { CalendarDays, ChevronDown, MapPin } from 'lucide-react';
import { BookingTourBasicDataDto } from '@/features/public/booking/types/BookingTourBasicDataDto.ts';
import {
  TourDetailsList,
  TourOrganizer,
  TourPriceList,
} from '@/features/public/booking/components/TourInfoSections.tsx';
import { formatLowestPriceHint } from '@/features/public/booking/components/tourInfoFormatters.ts';
import { formatDestinationFull } from '@/features/dashboard/tours/types/DestinationDto.utils.ts';
import timeUtils from '@/utils/timeUtils.ts';
import { cn } from '@/lib/utils.ts';

/**
 * Blok o wyjeździe na wąskim ekranie: zwinięty do jednego wiersza z terminem,
 * miejscem i ceną, rozwijany do pełnej treści kolumny z `OrgInfo`.
 *
 * Nazwy wyjazdu tu nie ma celowo — stoi tuż obok w nagłówku kroku i powtórzona
 * w tej samej wadze konkurowała z nim o pierwsze spojrzenie.
 */
const TourInfoBox = ({
  tourBasicInfo,
  className,
  ...props
}: {
  tourBasicInfo: BookingTourBasicDataDto;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();

  const priceHint = formatLowestPriceHint(tourBasicInfo.prices);
  const destination = tourBasicInfo.destination
    ? formatDestinationFull(tourBasicInfo.destination)
    : null;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-neutral-200 bg-basic-white',
        className,
      )}
      {...props}
    >
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
      >
        <span className="sr-only">Szczegóły wyjazdu</span>

        {tourBasicInfo.organizerLogoUrl && (
          <img
            src={tourBasicInfo.organizerLogoUrl}
            alt=""
            className="h-8 w-8 shrink-0 rounded object-contain"
          />
        )}

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
            <CalendarDays aria-hidden className="size-3.5 shrink-0" />
            <span className="truncate">
              {timeUtils.toDateRangeString(
                tourBasicInfo.startDate,
                tourBasicInfo.endDate,
              )}
            </span>
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-600">
            {destination && (
              <span className="flex min-w-0 items-center gap-1">
                <MapPin aria-hidden className="size-3.5 shrink-0" />
                <span className="truncate">{destination}</span>
              </span>
            )}
            {priceHint && (
              <span className="font-medium text-base-text">{priceHint}</span>
            )}
          </span>
        </span>

        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 shrink-0 text-neutral-500 transition-transform duration-200 motion-reduce:transition-none',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Wysokość animowana siatką (0fr → 1fr): treść zostaje w drzewie, więc
          `aria-controls` wskazuje na coś, co istnieje także po zwinięciu. */}
      <div
        id={panelId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden" inert={!isOpen}>
          <div className="flex flex-col gap-3 border-t border-neutral-100 p-3">
            <TourOrganizer tourBasicInfo={tourBasicInfo} showLogo={false} />

            {tourBasicInfo.prices && tourBasicInfo.prices.length > 0 && (
              <TourPriceList prices={tourBasicInfo.prices} />
            )}

            <TourDetailsList tourBasicInfo={tourBasicInfo} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourInfoBox;
